import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  createSchedule,
  removeSchedules,
  saveScheduleDevices,
  saveScheduleOperations,
  saveScheduleSettings,
  scheduleDevicesQuery,
  scheduleOperationsQuery,
  scheduleQuery,
  scheduleSettingsQuery,
  type ScheduleAlgorithm,
  type SchedulePicked,
} from '../api/opteemum'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { scheduleColumns } from './columns'
import { OpteemumTablePage } from './TablePage'

/**
 * Opteemum::schedule_management - `#opteemumScheduleManagement`.
 *
 * PE RAMURA ASTA ERA O LISTA MOARTA. Coloana intai desena semnul de plus si
 * ultima o coloana de bife, dar dedesubt nu era nimic si nicaieri niciun buton:
 * o programare nu se putea nici deschide, nici face, nici sterge. Metodele
 * plecasera cu `Opteemum.php`, deci nici nu mai era adevarat ca "Add / Remove
 * raman in CI3" - acolo nu mai era nimic.
 *
 * Filele sunt cele trei din `schedule_management/others/tabList.php`:
 * Operations (ce comenzi ruleaza), Settings (cand ruleaza) si Devices (pe ce
 * masini). Fiecare salvare trimite mai departe un anunt catre masinile
 * programarii - fara el, o programare schimbata ar fi ramas pe agent asa cum
 * era.
 */

type Tab = 'operations' | 'settings' | 'devices'

const TABS: { key: Tab; label: string }[] = [
  { key: 'operations', label: 'Operations' },
  { key: 'settings', label: 'Settings' },
  { key: 'devices', label: 'Devices' },
]

const DAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
]

function ScheduleTabs({ id }: { id: number }) {
  const [tab, setTab] = useState<Tab>('operations')

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {TABS.map((one) => (
            <li key={one.key}>
              <a
                href="#"
                className={`action_button${tab === one.key ? ' active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(one.key)
                }}
              >
                {one.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'operations' && <OperationsTab id={id} />}
          {tab === 'settings' && <SettingsTab id={id} />}
          {tab === 'devices' && <DevicesTab id={id} />}
        </div>
      </div>
    </div>
  )
}

/** schedule_management/others/operations.php */
function OperationsTab({ id }: { id: number }) {
  const page = useQuery(scheduleOperationsQuery(id))
  const client = useQueryClient()
  const [draft, setDraft] = useState<Record<number, SchedulePicked> | null>(null)
  const [saving, setSaving] = useState(false)

  if (page.isPending) return <Spinner />
  if (page.error) {
    return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  }
  if (!page.data) return null

  /* Ce e bifat acum: intai ce a schimbat omul, altfel ce a spus serverul. */
  const picked: Record<number, SchedulePicked> =
    draft ??
    Object.fromEntries(Object.entries(page.data.picked).map(([key, value]) => [Number(key), value]))

  const groupOf = (groupId: number): SchedulePicked => picked[groupId] ?? { inform_user: 0, commands: [] }

  const toggleCommand = (groupId: number, commandId: number, on: boolean) => {
    const group = groupOf(groupId)
    const commands = on
      ? [...group.commands, commandId]
      : group.commands.filter((one) => one !== commandId)
    setDraft({ ...picked, [groupId]: { ...group, commands } })
  }

  return (
    <form
      className="scheduleOperationsForm"
      onSubmit={(e) => {
        e.preventDefault()
        setSaving(true)
        void saveScheduleOperations(id, picked)
          .then(() => {
            toast('Schedule operations saved.', 'Success')
            setDraft(null)
            return client.invalidateQueries({ queryKey: ['opteemum', 'schedule', id] })
          })
          .catch((err: Error) => toast(err.message, 'Error', 'error'))
          .finally(() => setSaving(false))
      }}
    >
      {page.data.groups.length === 0 && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle"></i> There are no operations to schedule.
        </div>
      )}
      {page.data.groups.map((group) => {
        const chosen = groupOf(group.id)
        return (
          <div key={group.id}>
            <div className="col-md-12 divider">
              <h4 className="bordered">{group.name}</h4>
            </div>
            <div style={{ padding: 20 }}>
              <div className="row">
                <div className="col-md-6">
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {group.commands.map((command) => (
                      <li key={command.ID} style={{ marginBottom: 10 }}>
                        <SettingsCheck
                          className="check-inform"
                          checked={chosen.commands.includes(command.ID)}
                          onChange={(on) => toggleCommand(group.id, command.ID, on)}
                          label={command.command_title}
                        />{' '}
                        {command.info ? (
                          <i
                            className="fas fa-info-circle color-blue"
                            data-toggle="tooltip"
                            data-placement="top"
                            title={command.info}
                          ></i>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div style={{ marginTop: 20 }}>
                <SettingsCheck
                  className="inform_user"
                  checked={chosen.inform_user === 1}
                  onChange={(on) => setDraft({ ...picked, [group.id]: { ...chosen, inform_user: on ? 1 : 0 } })}
                  label="Inform user"
                />
              </div>
            </div>
          </div>
        )
      })}
      {page.data.groups.length > 0 && (
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      )}
    </form>
  )
}

/**
 * schedule_management/others/settings.php
 *
 * Cele patru feluri stau pe file, ca in macheta veche, si fiecare are bifa lui
 * de Activ. Numai cele APRINSE se trimit: asa scria si modelul vechi, si de-aia
 * un fel stins dispare din configurare in loc sa ramana acolo cu `active = 0`.
 */
function SettingsTab({ id }: { id: number }) {
  const page = useQuery(scheduleSettingsQuery(id))
  const client = useQueryClient()
  const [draft, setDraft] = useState<ScheduleAlgorithm | null>(null)
  const [kind, setKind] = useState<keyof ScheduleAlgorithm>('daily')
  const [saving, setSaving] = useState(false)

  if (page.isPending) return <Spinner />
  if (page.error) {
    return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  }
  if (!page.data) return null

  const algorithm: ScheduleAlgorithm = draft ?? page.data.algorithm
  const active = (type: keyof ScheduleAlgorithm) => Number(algorithm[type]?.active ?? 0) === 1
  const set = (type: keyof ScheduleAlgorithm, field: string, value: string) =>
    setDraft({ ...algorithm, [type]: { ...(algorithm[type] ?? {}), [field]: value } })
  const setActive = (type: keyof ScheduleAlgorithm, on: boolean) =>
    setDraft({ ...algorithm, [type]: { ...(algorithm[type] ?? {}), active: on ? '1' : '0' } })

  const activeBox = (type: keyof ScheduleAlgorithm) => (
    <div className="form-group col-sm-12">
      <SettingsCheck checked={active(type)} onChange={(on) => setActive(type, on)} label="Active" />
    </div>
  )

  const timeBox = (type: keyof ScheduleAlgorithm) => (
    <div className="form-group">
      <label className="col-sm-2">
        <span className="color">Time of day</span>
      </label>
      <div className="col-sm-4 inputGroupContainer">
        <div className="input-group">
          <input
            type="time"
            className="form-control"
            value={String((algorithm[type] as { time?: string } | undefined)?.time ?? '')}
            onChange={(e) => set(type, 'time', e.target.value)}
          />
          <span className="input-group-addon">
            <i className="far fa-clock"></i>
          </span>
        </div>
      </div>
    </div>
  )

  return (
    <form
      className="scheduleSettingsForm form-horizontal labelcustomize"
      onSubmit={(e) => {
        e.preventDefault()
        setSaving(true)
        void saveScheduleSettings(id, algorithm)
          .then(() => {
            toast('Schedule settings saved.', 'Success')
            setDraft(null)
            return client.invalidateQueries({ queryKey: ['opteemum', 'schedule', id] })
          })
          .catch((err: Error) => toast(err.message, 'Error', 'error'))
          .finally(() => setSaving(false))
      }}
    >
      <ul role="tablist" className="nav nav-tabs">
        {(['daily', 'weekly', 'monthly', 'once'] as const).map((one) => (
          <li key={one} role="presentation" className={kind === one ? 'active' : ''}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setKind(one)
              }}
            >
              {one === 'once' ? 'Only Once' : one.charAt(0).toUpperCase() + one.slice(1)}
              {active(one) ? ' •' : ''}
            </a>
          </li>
        ))}
      </ul>
      <div className="tab-content" style={{ paddingTop: 15 }}>
        {kind === 'daily' && (
          <div role="tabpanel">
            {activeBox('daily')}
            {timeBox('daily')}
          </div>
        )}
        {kind === 'weekly' && (
          <div role="tabpanel">
            {activeBox('weekly')}
            <div className="form-group">
              <label className="col-sm-2">
                <span className="color">Day of week</span>
              </label>
              <div className="col-sm-4 selectContainer">
                <select
                  className="form-control"
                  value={String(algorithm.weekly?.day ?? '')}
                  onChange={(e) => set('weekly', 'day', e.target.value)}
                >
                  <option value="">Select..</option>
                  {DAYS.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {timeBox('weekly')}
          </div>
        )}
        {kind === 'monthly' && (
          <div role="tabpanel">
            {activeBox('monthly')}
            <div className="form-group">
              <label className="col-sm-2">
                <span className="color">Day of month</span>
              </label>
              <div className="col-sm-4 selectContainer">
                <select
                  className="form-control"
                  value={String(algorithm.monthly?.day ?? '')}
                  onChange={(e) => set('monthly', 'day', e.target.value)}
                >
                  <option value="">Select..</option>
                  {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                  <option value="last_day">Last day</option>
                </select>
              </div>
            </div>
            {timeBox('monthly')}
          </div>
        )}
        {kind === 'once' && (
          <div role="tabpanel">
            {activeBox('once')}
            <div className="form-group">
              <label className="col-sm-2">
                <span className="color">Date</span>
              </label>
              <div className="col-sm-4 inputGroupContainer">
                <div className="input-group">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={String(algorithm.once?.datetime ?? '').replace(' ', 'T')}
                    onChange={(e) => set('once', 'datetime', e.target.value.replace('T', ' '))}
                  />
                  <span className="input-group-addon">
                    <i className="fa fa-calendar"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <button type="submit" className="btn btn-primary saveScheduleSettings" disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </form>
  )
}

/** schedule_management/others/devices.php */
function DevicesTab({ id }: { id: number }) {
  const page = useQuery(scheduleDevicesQuery(id))
  const client = useQueryClient()
  const [draft, setDraft] = useState<number[] | null>(null)
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  if (page.isPending) return <Spinner />
  if (page.error) {
    return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  }
  if (!page.data) return null

  const picked = draft ?? page.data.picked
  const needle = search.trim().toLowerCase()
  const shown = needle === ''
    ? page.data.devices
    : page.data.devices.filter((d) => (d.Denumire + ' ' + d.UserLogat).toLowerCase().includes(needle))

  return (
    <form
      className="scheduleDevicesForm form-horizontal labelcustomize"
      onSubmit={(e) => {
        e.preventDefault()
        setSaving(true)
        void saveScheduleDevices(id, picked)
          .then(() => {
            toast('Schedule devices saved.', 'Success')
            setDraft(null)
            return client.invalidateQueries({ queryKey: ['opteemum', 'schedule', id] })
          })
          .catch((err: Error) => toast(err.message, 'Error', 'error'))
          .finally(() => setSaving(false))
      }}
    >
      <div className="form-group">
        <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
          <span className="color">Devices</span>
        </label>
        <div className="col-sm-9">
          {/* Cautarea tine locul lui `data-live-search` din bootstrap-select:
              o firma cu doua sute de masini nu se rasfoieste cu ochiul. */}
          <input
            type="search"
            className="form-control input-sm"
            placeholder="Search devices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <select
            className="form-control"
            multiple
            size={10}
            value={picked.map(String)}
            onChange={(e) => setDraft(Array.from(e.target.selectedOptions, (o) => Number(o.value)))}
          >
            {shown.map((device) => (
              <option key={device.ID} value={device.ID}>
                {device.Denumire}
                {device.UserLogat ? ` (${device.UserLogat})` : ''}
              </option>
            ))}
          </select>
          <span className="color-light-grey font-11">{picked.length} selected</span>
        </div>
      </div>
      <button type="submit" className="btn btn-primary" disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </form>
  )
}

export function SchedulePage() {
  const client = useQueryClient()
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [addOpen, setAddOpen] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = () => client.invalidateQueries({ queryKey: ['opteemum', 'schedule'] })

  return (
    <>
      <OpteemumTablePage
        id="opteemumScheduleManagement"
        columns={scheduleColumns()}
        queryOf={scheduleQuery}
        details={(row) => <ScheduleTabs id={Number(row.schedule_id)} />}
        selected={selected}
        onSelected={setSelected}
        rowKey={(r) => Number(r.schedule_id)}
        headerButtons={
          <>
            {/* `add-schedule` si `remove-schedules` sunt clasele din
                `ScheduleManagementButtons`; Remove sta stins pana se bifeaza
                ceva, ca si acolo. */}
            <a
              className="btn btn-success add-schedule tk-header-extra"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setName('')
                setAddOpen(true)
              }}
            >
              <i className="fas fa-plus"></i> Add schedule
            </a>{' '}
            <a
              className={`btn btn-danger remove-schedules tk-header-extra${selected.size === 0 ? ' disabled' : ''}`}
              href="#"
              onClick={(e) => {
                e.preventDefault()
                if (selected.size === 0 || busy) return
                if (!window.confirm('Are you sure? This schedules will not run anymore!')) return
                setBusy(true)
                void removeSchedules([...selected])
                  .then(() => {
                    toast('Schedules removed.', 'Success')
                    setSelected(new Set())
                    return refresh()
                  })
                  .catch((err: Error) => toast(err.message, 'Error', 'error'))
                  .finally(() => setBusy(false))
              }}
            >
              <i className="fas fa-trash"></i> Remove schedules
            </a>
          </>
        }
      />
      <Modal id="addScheduleModal" open={addOpen} title="Create schedule" onClose={() => setAddOpen(false)}>
        <div className="form-group">
          <label>
            <span className="color">Schedule name</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="modal-footer" style={{ margin: '15px -15px -15px', padding: '15px' }}>
          <button type="button" className="btn btn-default" onClick={() => setAddOpen(false)}>
            Close
          </button>{' '}
          <button
            type="button"
            className="btn btn-primary createSchedule"
            disabled={busy || name.trim() === ''}
            onClick={() => {
              setBusy(true)
              void createSchedule(name.trim())
                .then(() => {
                  toast('New schedule created!', 'Success')
                  setAddOpen(false)
                  return refresh()
                })
                .catch((err: Error) => toast(err.message, 'Error', 'error'))
                .finally(() => setBusy(false))
            }}
          >
            Save
          </button>
        </div>
      </Modal>
    </>
  )
}
